/**
 * ATHÉR product rails on the product page.
 *
 * "You may also like" ([data-ather-related]) arrives rendered: Liquid ranks the
 * products by how many real collections they share with this one. Here
 * Shopify's own recommendations only break ties - cards with the same score
 * are put in Shopify's order - and never add a product. The reorder happens
 * only while the rail is below the screen, so nothing moves in view.
 *
 * "Recently viewed" ([data-ather-recent]) reads Horizon's history
 * (localStorage "viewedProducts", assets/recently-viewed-products.js; nothing
 * is sent anywhere but the store's own search) and, once the section is near
 * the screen, fetches every card in one request: the search page's
 * "ather-recent" view (templates/search.ather-recent.json, no layout), so
 * price, image and availability are the store's current ones. The open
 * product is left out; with nothing to show the section stays hidden.
 *
 * Both rails then get the shared carousel nav and, when the motion layer is
 * on, a one-time fade in.
 */
(function () {
  'use strict';

  // both rails include this file; run it once
  if (window.__atherRails) return;
  window.__atherRails = true;

  var HISTORY_KEY = 'viewedProducts';
  var reduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function history() {
    try {
      var list = JSON.parse(window.localStorage.getItem(HISTORY_KEY) || '[]');
      return Array.isArray(list) ? list.map(String) : [];
    } catch (e) {
      return [];
    }
  }

  // From 990px Horizon scrolls .page-wrapper, not the window. An observer's
  // look-ahead margin only works on the element that actually scrolls.
  function scrollRoot() {
    var w = document.querySelector('.page-wrapper');
    if (!w) return null;
    var o = getComputedStyle(w).overflowY;
    return (o === 'auto' || o === 'scroll') && w.scrollHeight > w.clientHeight + 1 ? w : null;
  }

  function inView(el) {
    var r = el.getBoundingClientRect();
    return r.top < (window.innerHeight || 0) && r.bottom > 0;
  }

  /* ---------------------------------------------------------------- reveal */

  function arm(rail) {
    if (!rail.hasAttribute('data-ather-motion') || rail.classList.contains('is-armed')) return;
    if (!document.documentElement.classList.contains('ather-motion')) return;
    if ((reduce && reduce.matches) || typeof IntersectionObserver !== 'function') return;
    var items = rail.querySelectorAll('.ather-rail__item');
    for (var i = 0; i < items.length; i++) items[i].style.setProperty('--i', String(i));
    // already on screen: show at once rather than blink out and back
    if (inView(rail)) return;
    rail.classList.add('is-armed');
    var io = new IntersectionObserver(
      function (entries) {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        var all = rail.querySelectorAll('.ather-rail__item');
        for (var k = 0; k < all.length; k++) all[k].classList.add('is-in');
      },
      { root: scrollRoot(), rootMargin: '0px 0px -8% 0px' }
    );
    io.observe(rail);
  }

  function wire(section) {
    var rail = section.querySelector('.ather-rail');
    if (!rail) return;
    if (window.AtherCarouselNav) window.AtherCarouselNav.scan(section.closest('.shopify-section') || section);
    arm(rail);
  }

  /* ------------------------------------------------------ you may also like */

  function related(section) {
    var track = section.querySelector('.ather-rail__track');
    var url = section.getAttribute('data-recommendations-url');
    var id = section.getAttribute('data-product-id');
    if (!track || !url || !id) return wire(section);

    // On screen at load: keep the cards invisible (their space stays reserved,
    // so nothing shifts) until Shopify's ranking has put them in final order.
    var hold = inView(section);
    if (hold) section.classList.add('is-ranking');
    var released = false;
    var release = function () {
      if (released) return;
      released = true;
      section.classList.remove('is-ranking');
    };
    if (hold) window.setTimeout(release, 1500);

    fetch(url + '.json?product_id=' + encodeURIComponent(id) + '&limit=10&intent=related', { priority: 'low' })
      .then(function (r) {
        return r.ok ? r.json() : { products: [] };
      })
      .catch(function () {
        return { products: [] };
      })
      .then(function (data) {
        var rank = {};
        (data.products || []).forEach(function (p, i) {
          rank[String(p.id)] = i + 1;
        });
        var items = Array.prototype.slice.call(track.children);
        items.forEach(function (li) {
          var r = rank[li.getAttribute('data-product-id')];
          if (r) li.setAttribute('data-ather-rec-rank', String(r));
        });
        if ((!hold && !inView(section)) || (hold && !released)) {
          // stable: score first (Liquid's order), then Shopify's rank, then Liquid's order
          var sorted = items
            .map(function (li, i) {
              return { li: li, i: i, s: +li.getAttribute('data-ather-score') || 0, r: rank[li.getAttribute('data-product-id')] || 999 };
            })
            .sort(function (a, b) {
              return b.s - a.s || a.r - b.r || a.i - b.i;
            });
          var changed = sorted.some(function (x, i) {
            return x.i !== i;
          });
          if (changed) {
            // Chrome keeps the card it last snapped to and snaps back to it after
            // a reorder (two or three cards along): reorder with snapping off,
            // return to the start, then snap again from the new first card
            track.style.scrollSnapType = 'none';
            sorted.forEach(function (x) {
              track.appendChild(x.li);
            });
            track.scrollLeft = 0;
            window.requestAnimationFrame(function () {
              track.style.scrollSnapType = '';
            });
          }
          section.setAttribute('data-ather-tiebreak', changed ? 'reordered' : 'kept');
        } else {
          // the 1.5s limit passed first: the visible order stays as it is
          section.setAttribute('data-ather-tiebreak', 'skipped-late');
        }
        release();
        wire(section);
      });
  }

  /* -------------------------------------------------------- recently viewed */

  function recent(section) {
    var current = section.getAttribute('data-product-id');
    var limit = parseInt(section.getAttribute('data-limit'), 10) || 8;
    var ids = history()
      .filter(function (id, i, a) {
        return id && id !== current && a.indexOf(id) === i;
      })
      .slice(0, limit);
    if (!ids.length) return;

    var load = function () {
      var url = new URL(section.getAttribute('data-search-url'), window.location.origin);
      url.searchParams.set('view', 'ather-recent');
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[unavailable_products]', 'last');
      url.searchParams.set('q', ids.map(function (id) { return 'id:' + id; }).join(' OR '));

      fetch(url.toString())
        .then(function (r) {
          return r.ok ? r.text() : '';
        })
        .catch(function () {
          return '';
        })
        .then(function (html) {
          if (!html) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var found = {};
          Array.prototype.forEach.call(doc.querySelectorAll('[data-ather-recent-results] > .ather-rail__item'), function (li) {
            found[li.getAttribute('data-product-id')] = li;
          });
          var track = section.querySelector('.ather-rail__track');
          var count = 0;
          ids.forEach(function (id) {
            if (!found[id] || id === current) return;
            // cards come from the search page: drop its click-tracking parameters
            Array.prototype.forEach.call(found[id].querySelectorAll('a[href*="_pos="], a[href*="_sid="]'), function (a) {
              var u = new URL(a.getAttribute('href'), window.location.origin);
              ['_pos', '_sid', '_ss', '_psq', '_fid'].forEach(function (k) { u.searchParams.delete(k); });
              a.setAttribute('href', u.pathname + u.search + u.hash);
            });
            track.appendChild(document.adoptNode(found[id]));
            count++;
          });
          if (!count) return;
          section.setAttribute('data-ather-count', String(count));
          section.hidden = false;
          wire(section);
        });
    };

    if (typeof IntersectionObserver !== 'function') return load();
    var io = new IntersectionObserver(
      function (entries) {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        load();
      },
      // a hidden section has no size, and a zero-size target never reports as
      // intersecting: watch the footer that follows it instead
      { root: scrollRoot(), rootMargin: '0px 0px 900px 0px' }
    );
    io.observe(document.querySelector('footer') || section.parentElement || section);
  }

  function init(root) {
    var scope = root || document;
    Array.prototype.forEach.call(scope.querySelectorAll('[data-ather-related]'), related);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-ather-recent]'), recent);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
